import React, { useContext, useEffect, useState, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from "react-router-dom";
import { BreadcrumbContext } from '../../../../components/Breadcrumb';
import { AuthContext } from "../../../../components/FirebaseAuth";
import { CloudFunctions } from "../../../../components/FirebaseAuth/firebase";
import Loader from "../../../../components/Loader";
import {Form} from "../../../../components/Form";
import { Card, CardContent, Alert, Select, MenuItem, FormControl, InputLabel, Stack, Button, Typography, Avatar } from "@mui/material";

const UserRole = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { userData } = useContext(AuthContext);
    const { userId } = useParams();
    const mountedRef = useRef(true);
    const { setBreadcrumb } = useContext(BreadcrumbContext);
    const [user, setUser] = useState(null);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [inSubmit, setInSubmit] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);

    useEffect(() => {
        setBreadcrumb([
            {
                to: "/",
                text: t('common.home'),
                active: false
            },
            {
                to: "/account/"+userData.currentAccount.id+"/",
                text: userData.currentAccount.name,
                active: false
            },
            {
                to: "/account/"+userData.currentAccount.id+"/users",
                text: t('addUser.manageUsersBreadcrumb'),
                active: false
            },
            {
                to: null,
                text: t('userRole.title'),
                active: true
            }
        ]);
        setError(null);
        const getAccountUser = CloudFunctions.httpsCallable('getAccountUser');
        getAccountUser({
            accountId: userData.currentAccount.id,
            userId: userId
        }).then(res => {
            if (!mountedRef.current) return null
            res.data.lastLoginTime = new Date(res.data.lastLoginTime);
            setUser(res.data);
        }).catch(err => {
            if (!mountedRef.current) return null
            setError(err.message);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[userData, userId, setBreadcrumb, t]);

    useEffect(() => {
        return () => {
            mountedRef.current = false
        }
    },[]);

    return (
        <Card
            sx={{
                backgroundColor: (theme) => theme.custom.card,
                boxShadow: (theme) => theme.custom.cardShadow,
                border: (theme) => `1px solid ${theme.custom.border}`,
            }}
        >
            <CardContent>
            {(userId !== userData.currentAccount.owner)?(
                success?(
                    <>
                        <Alert severity="success" onClose={() => setSuccess(false)}>{t('userRole.updateSuccess')}</Alert>
                        <Stack direction="row" spacing={1} mt={2}>
                            <Button variant="contained" color="primary" onClick={() => navigate("/account/"+userData.currentAccount.id+"/users")} >{t('userRole.backToUsers')}</Button>
                        </Stack>
                    </>

                ):(
                    <Stack spacing={3}>
                        {error !== null &&
                            <Alert severity="error">{error}</Alert>
                        }
                        {user === null ? (
                            <Loader text={t('userRole.loadingUserDetails')} />
                        ):(
                            <Form handleSubmit={e => {
                                e.preventDefault();
                                setError(null);
                                setSuccess(false);
                                setInSubmit(true);
                                const updateAccountUserRole = CloudFunctions.httpsCallable('updateAccountUserRole');
                                updateAccountUserRole({
                                    accountId: userData.currentAccount.id,
                                    userId: userId,
                                    role: selectedRole
                                }).then(res => {
                                    setInSubmit(false);
                                    setSuccess(true);
                                }).catch(err => {
                                    setInSubmit(false);
                                    setError(err.message);
                                });
                            }}
                            disabled={selectedRole===null || selectedRole===user.role || inSubmit}
                            submitBtnStyle={(selectedRole!=='remove')?"primary":"error"}
                            inSubmit={inSubmit}
                            enableDefaultButtons={true}
                            backToUrl={"/account/"+userData.currentAccount.id+"/users"}
                            >
                                <Stack spacing={1} mb={5} style={{display: 'inline-block', textAlign: 'center'}}>
                                    <Avatar alt={user.displayName} src={user.photoUrl} sx={{width: 100, height:100, margin: 'auto'}} />
                                    <Typography><strong style={{fontSize: '1.3rem'}}>{user.displayName}</strong></Typography>
                                    <Typography>
                                        {t('userRole.lastLogin')}<br /> {user.lastLoginTime.toLocaleString()}
                                    </Typography>
                                </Stack>
                                <FormControl fullWidth>
                                    <InputLabel>{t('userRole.role')}</InputLabel>
                                    <Select label={t('userRole.role')} defaultValue={user.role} onChange={e => {
                                        setSelectedRole(e.target.value);
                                    }}>
                                        <MenuItem value="user">{t('addUser.roleUser')}</MenuItem>
                                        <MenuItem value="admin">{t('addUser.roleAdmin')}</MenuItem>
                                        <MenuItem value="remove"><em>{t('userRole.removeAccess')}</em></MenuItem>
                                    </Select>
                                </FormControl>
                            </Form>
                        )}
                    </Stack>
                )
            ):(
                <>
                    <Alert severity="error">{t('userRole.cannotChangeOwner')}</Alert>
                    <Stack direction="row" spacing={1} mt={2}>
                        <Button variant="contained" color="primary" onClick={() => navigate("/account/"+userData.currentAccount.id+"/users")} >{t('userRole.backToUsers')}</Button>
                    </Stack>
                </>
            )}
            </CardContent>
        </Card>
    )
}

export default UserRole;
